import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { replace } from 'redux-first-history';
import { stringify, parse } from 'query-string';
import { SimplePopover, SimplePopoverPlainOption, DateDropdown } from '@dev/ee-components';
import styled from 'styled-components';
import { DevicesParams, DevicesChannelParams } from '@api/services/ElasticApi';
import dayjs from '@api/services/tools/dayjs';
import { useDateFilterData } from '@api/services/tools/date';
import { MAX_CHANNELS_LIST_LENGTH, SHORT_DATE_FORMAT } from '@api/services/constants';
import { DateDropdownOptions } from '@api/store/suppressions/types';
import { ApplicationState as State } from '@api/store';
import { debounce } from '@api/services/tools/debounce';
import { FiltersInterface } from '../DeviceDetailsInterface';
import { useMobile } from '@api/services/hooks/useMobile';
import MobileFiltersPopover from '../MobileFiltersPopover';
import { getDateDropdownValue } from '@api/services/tools/getDateDropdownValue';

const FiltersStyled = styled.div`
    display: flex;
    gap: 16px;
`;

const FilterChannelsStyled = styled.div`
    max-width: 300px;
`;

type StyledProps = {
    isSmallScreen?: boolean;
};

const DateDropdownWrapper = styled.div<StyledProps>`
    #datedropdown-popover {
        & > div {
            max-height: ${({ isSmallScreen }): number => (isSmallScreen ? 273 : 600)}px;
        }
    }
`;

type RequestParams = DevicesParams | DevicesChannelParams;

interface DateDropdownFilterInterface {
    params: RequestParams;
    onCalendarChange: (startDate?: Date, endDate?: Date) => void;
}

const DateDropdownFilter: FC<DateDropdownFilterInterface> = (props: DateDropdownFilterInterface): JSX.Element => {
    const { t } = useTranslation();
    const { onCalendarChange } = props;
    const query = parse(location.search);
    const startDate = query?.from?.toString();
    const endDate = startDate && query?.to?.toString();

    const onItemClickDateRange = (ev: React.MouseEvent<HTMLElement>, { value }: Record<string, string | number>): void => {
        const dateFrom = dayjs()
            .subtract(value as number, 'day')
            .startOf('day')
            .toDate();
        const endDate = dayjs().endOf('day').toDate();
        const startDate = value === DateDropdownOptions.All ? null : dateFrom;

        onCalendarChange(startDate, endDate);
    };

    const isSmallScreen = useSelector((state: State) => state?.browser?.lessThan.lg);

    return (
        <DateDropdownWrapper isSmallScreen={isSmallScreen}>
            <DateDropdown
                options={useDateFilterData()}
                defaultPlaceholder={t('last3months')}
                startDate={startDate}
                endDate={endDate}
                value={getDateDropdownValue(false, DateDropdownOptions.Last3months)}
                type="input"
                returnRawData
                calendarOnChange={onCalendarChange}
                onItemClick={onItemClickDateRange}
                buttonVariant="tertiary"
                placement="bottom-end"
            />
        </DateDropdownWrapper>
    );
};

export const channelsOptions = (channels, loadingKey, resultKey): SimplePopoverPlainOption[] => {
    if (channels?.loading) {
        return [{ label: loadingKey, value: 'loading', disabled: true }];
    }
    if (channels?.channels?.length) {
        return channels?.channels.map((item) => ({ label: item.Name, value: item.Name }));
    }
    return [{ label: `0 ${resultKey}`, value: 'noresults', disabled: true }];
};

const Filters: FC<FiltersInterface> = (props: FiltersInterface): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const {
        from,
        to,
        selectedChannelName,
        queryChannelName,
        setSelectedChannelName,
        setQueryChannelName,
        setFrom,
        setTo,
        hasViewChannelsAL,
    } = props;

    const channels = useSelector((state: State) => state?.channels);
    const searchOption: SimplePopoverPlainOption = { label: 'search', value: 'search' };
    const allChannelsOption: SimplePopoverPlainOption = { label: t('allchannels'), value: '' };

    const handlePopoverItemClick = (value: string): void => {
        setSelectedChannelName(value as string);
        const urlQueryString = {
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            to: dayjs(to).format(SHORT_DATE_FORMAT),
            ...(!!value ? { channelName: value } : {}),
        };
        dispatch(replace({ search: stringify(urlQueryString as any) }));
    };

    const handleSearchChannel = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const query = event?.target?.value ?? '';
        debounce((): void => {
            setQueryChannelName(query);
        });
    };

    const onCalendarChange = (startDate?: Date, endDate?: Date): void => {
        setTo(endDate?.toJSON() ?? '');
        setFrom(startDate?.toJSON() ?? '');
        const urlQueryString = {
            ...(startDate ? { from: dayjs(startDate).format(SHORT_DATE_FORMAT) } : {}),
            to: dayjs(endDate).format(SHORT_DATE_FORMAT),
        };
        dispatch(replace({ search: stringify(urlQueryString as any) }));
    };

    return isMobile ? (
        <MobileFiltersPopover
            from={from}
            to={to}
            setFrom={setFrom}
            setTo={setTo}
            selectedChannelName={selectedChannelName}
            setSelectedChannelName={setSelectedChannelName}
            setQueryChannelName={setQueryChannelName}
            queryChannelName={queryChannelName}
            hasViewChannelsAL={hasViewChannelsAL}
        />
    ) : hasViewChannelsAL ? (
        <FiltersStyled>
            <FilterChannelsStyled>
                <SimplePopover
                    type="noborder"
                    disableSearch={!queryChannelName && channels?.totalCount <= MAX_CHANNELS_LIST_LENGTH}
                    options={[searchOption, allChannelsOption, ...channelsOptions(channels, t('loading'), t('results'))]}
                    enableCheckbox={false}
                    visibleScrollbar={true}
                    maxHeight={400}
                    fullWidth={true}
                    searchPhrase={queryChannelName}
                    keepSearchPhrase={true}
                    defaultValue={selectedChannelName}
                    defaultPlaceholder={t('allchannels')}
                    onItemClick={(e, obj): void => {
                        handlePopoverItemClick(obj.value as string);
                    }}
                    onOptionSearch={handleSearchChannel}
                    listItemWidth="400px"
                    listItemMaxLines={3}
                    placement="bottom-end"
                />
            </FilterChannelsStyled>
            <DateDropdownFilter params={{ from, to, channelName: selectedChannelName }} onCalendarChange={onCalendarChange} />
        </FiltersStyled>
    ) : (
        <FiltersStyled>
            <DateDropdownFilter params={{ from, to, channelName: selectedChannelName }} onCalendarChange={onCalendarChange} />
        </FiltersStyled>
    );
};

export default Filters;
